import React, { useEffect, useState } from "react";
import { PartnerLayout } from "./PartnerLayout";
import { Input } from "../ui/input";
import { Button } from "../ui/button";
import { Label } from "../ui/label";
import { Switch } from "../ui/switch";
import partnerApi from "../../lib/partnerApi";
import { toast } from "sonner";
import { Send, CheckCircle2, AlertCircle } from "lucide-react";

const ALL_ASSETS = ["BTC", "ETH", "USDT", "USDC", "SOL", "XRP", "ADA", "DOGE", "MATIC", "DOT"];

export default function PartnerSettings() {
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);

  useEffect(() => {
    partnerApi.get("/partner/me").then((r) => {
      setForm({
        name: r.data.name || "",
        webhook_url: r.data.webhook_url || "",
        allowed_assets: r.data.allowed_assets || [],
        allowed_payment_methods: r.data.allowed_payment_methods || ["sepa", "card"],
        min_amount_eur: r.data.min_amount_eur || "",
        max_amount_eur: r.data.max_amount_eur || "",
        primary_color: r.data.primary_color || "#4F46FF",
        logo_url: r.data.logo_url || "",
        fee_bps: r.data.fee_bps,
      });
    });
  }, []);

  if (!form) return <PartnerLayout title="Settings"><div className="text-sm text-muted-foreground">Loading…</div></PartnerLayout>;

  const save = async () => {
    setSaving(true);
    try {
      const payload = { ...form };
      payload.min_amount_eur = payload.min_amount_eur === "" ? null : Number(payload.min_amount_eur);
      payload.max_amount_eur = payload.max_amount_eur === "" ? null : Number(payload.max_amount_eur);
      delete payload.fee_bps;
      await partnerApi.patch("/partner/settings", payload);
      toast.success("Settings saved");
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
    finally { setSaving(false); }
  };

  const sendTest = async () => {
    setTesting(true); setTestResult(null);
    try {
      const r = await partnerApi.post("/partner/test-webhook", { event: "order.completed" });
      setTestResult(r.data);
      if (r.data.delivered) toast.success("Webhook delivered");
      else toast.error("Webhook failed — see response below");
    } catch (e) { toast.error(e?.response?.data?.detail || "Test failed"); }
    finally { setTesting(false); }
  };

  const toggleAsset = (a) => {
    const has = form.allowed_assets.includes(a);
    setForm({ ...form, allowed_assets: has ? form.allowed_assets.filter((x) => x !== a) : [...form.allowed_assets, a] });
  };
  const togglePM = (p) => {
    const has = form.allowed_payment_methods.includes(p);
    setForm({ ...form, allowed_payment_methods: has ? form.allowed_payment_methods.filter((x) => x !== p) : [...form.allowed_payment_methods, p] });
  };

  return (
    <PartnerLayout title="Settings"
      subtitle="Display, webhooks, allowed assets, branding"
      actions={<Button onClick={save} disabled={saving} className="bg-primary hover:bg-primary/90 text-white rounded-xl" data-testid="settings-save-btn">{saving ? "Saving…" : "Save changes"}</Button>}>
      <div className="grid md:grid-cols-2 gap-5">
        <section className="xch-card p-5 space-y-3" data-testid="section-general">
          <SectionTitle title="General" />
          <Field label="Display name"><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="rounded-xl" data-testid="settings-name-input" /></Field>
          <Field label="Fee mark-up (bps)"><Input value={form.fee_bps} disabled className="rounded-xl font-mono bg-muted" data-testid="settings-fee-input" /><p className="text-[10px] text-muted-foreground">Editable by staff in admin.</p></Field>
        </section>

        <section className="xch-card p-5 space-y-3" data-testid="section-webhook">
          <SectionTitle title="Webhook" />
          <Field label="Webhook URL"><Input value={form.webhook_url} onChange={(e) => setForm({ ...form, webhook_url: e.target.value })} placeholder="https://api.yoursite.com/xchangely" className="rounded-xl font-mono text-xs" data-testid="settings-webhook-input" /></Field>
          <Button variant="outline" onClick={sendTest} disabled={testing || !form.webhook_url} className="rounded-xl gap-1.5" data-testid="settings-test-webhook-btn">
            <Send className="w-3.5 h-3.5" /> {testing ? "Sending…" : "Send test webhook"}
          </Button>
          {testResult && (
            <div className={`text-xs rounded-xl p-3 space-y-1 ${testResult.delivered ? "bg-emerald-50 text-emerald-900" : "bg-rose-50 text-rose-900"}`} data-testid="test-result">
              <div className="flex items-center gap-1.5 font-semibold">
                {testResult.delivered ? <CheckCircle2 className="w-4 h-4" /> : <AlertCircle className="w-4 h-4" />}
                {testResult.delivered ? "Delivered" : "Failed"} · HTTP {testResult.status || "—"} · {testResult.latency_ms || "?"}ms · {testResult.attempts} attempts
              </div>
              {testResult.response_body && <pre className="font-mono text-[10px] bg-white/60 rounded p-1.5 overflow-x-auto">{testResult.response_body}</pre>}
              {testResult.error && <div className="font-mono text-[10px]">{testResult.error}</div>}
            </div>
          )}
        </section>

        <section className="xch-card p-5 space-y-3" data-testid="section-assets">
          <SectionTitle title="Allowed assets" subtitle="Empty = all assets allowed" />
          <div className="grid grid-cols-5 gap-1.5">
            {ALL_ASSETS.map((a) => {
              const on = form.allowed_assets.includes(a);
              return (
                <button key={a} onClick={() => toggleAsset(a)}
                        className={`text-xs font-bold py-2 rounded-lg border transition-colors ${on ? "bg-primary text-white border-primary" : "bg-white text-foreground border-border hover:border-primary/40"}`}
                        data-testid={`asset-toggle-${a}`}>{a}</button>
              );
            })}
          </div>
        </section>

        <section className="xch-card p-5 space-y-3" data-testid="section-methods">
          <SectionTitle title="Payment methods & limits" />
          <div className="flex gap-2">
            {["sepa", "card"].map((p) => {
              const on = form.allowed_payment_methods.includes(p);
              return (
                <button key={p} onClick={() => togglePM(p)}
                        className={`flex-1 text-sm font-bold py-2 rounded-xl border transition-colors uppercase ${on ? "bg-primary text-white border-primary" : "bg-white text-foreground border-border"}`}
                        data-testid={`pm-toggle-${p}`}>{p}</button>
              );
            })}
          </div>
          <div className="grid grid-cols-2 gap-2">
            <Field label="Min EUR"><Input type="number" value={form.min_amount_eur} onChange={(e) => setForm({ ...form, min_amount_eur: e.target.value })} className="rounded-xl" data-testid="settings-min-input" /></Field>
            <Field label="Max EUR"><Input type="number" value={form.max_amount_eur} onChange={(e) => setForm({ ...form, max_amount_eur: e.target.value })} className="rounded-xl" data-testid="settings-max-input" /></Field>
          </div>
        </section>

        <section className="xch-card p-5 space-y-3 md:col-span-2" data-testid="section-branding">
          <SectionTitle title="Branding" subtitle="Applied to your embed widget" />
          <div className="grid md:grid-cols-2 gap-3">
            <Field label="Primary color">
              <div className="flex items-center gap-2">
                <input type="color" value={form.primary_color} onChange={(e) => setForm({ ...form, primary_color: e.target.value })}
                       className="w-12 h-10 rounded-xl border border-border cursor-pointer" data-testid="settings-color-input" />
                <Input value={form.primary_color} onChange={(e) => setForm({ ...form, primary_color: e.target.value })} className="rounded-xl font-mono" />
              </div>
            </Field>
            <Field label="Logo URL"><Input value={form.logo_url} onChange={(e) => setForm({ ...form, logo_url: e.target.value })} placeholder="https://…" className="rounded-xl text-xs" data-testid="settings-logo-input" /></Field>
          </div>
        </section>
      </div>
    </PartnerLayout>
  );
}

const Field = ({ label, children }) => (
  <div className="space-y-1.5">
    <Label className="text-[10px] uppercase tracking-wider font-semibold text-muted-foreground">{label}</Label>
    {children}
  </div>
);
const SectionTitle = ({ title, subtitle }) => (
  <div>
    <div className="font-display text-base font-bold text-foreground">{title}</div>
    {subtitle && <p className="text-xs text-muted-foreground mt-0.5">{subtitle}</p>}
  </div>
);
